/**
 * Referrals Page - Main Orchestrator
 * Refer a friend and earn rewards
 */

import { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';

// Hooks
import { useReferralData } from './hooks/useReferralData';

// Components
import {
  ReferralHero,
  ReferralCodeCard,
  SocialShareButtons,
  RewardsCard,
  QualificationCard,
  PrintMaterialsCard,
  RecentReferrals,
  ReferralTips,
} from './components';

// Utils
import { calculateRewardsLeft, getReferralUrl } from './utils/helpers';
import { REFERRAL_REWARDS } from './utils/constants';

// Types
import type { ReferralHeroStats } from './types';

export default function ReferralsPage() {
  const { t } = useLanguage();

  // Fetch all referral data
  const { stats, referralCode, requirements, isLoading, error } = useReferralData();

  // Page title
  useEffect(() => {
    document.title = t('referrals.pageTitle', 'Refer a Friend - Rumio');
  }, [t]);

  // Calculate hero stats
  const heroStats: ReferralHeroStats = {
    totalReferrals: stats?.totalReferrals ?? 0,
    qualifiedReferrals: stats?.qualifiedReferrals ?? 0,
    pendingReferrals: stats?.pendingReferrals ?? 0,
    rewardsLeft: calculateRewardsLeft(
      stats?.rewardsThisMonth ?? 0,
      REFERRAL_REWARDS.MONTHLY_CAP
    ),
  };

  const referralUrl = getReferralUrl(referralCode);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-500">{t('common.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">!</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t('common.error', 'Something went wrong')}
          </h2>
          <p className="text-gray-500 mb-4">
            {t('referrals.loadError', 'Unable to load your referral information. Please try again.')}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            {t('common.retry', 'Try Again')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <ReferralHero
        stats={heroStats}
        premiumDaysRemaining={stats?.premiumDaysRemaining}
        premiumExpiresAt={stats?.premiumExpiresAt}
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 -mt-4 space-y-6">
        {/* Code Card with QR */}
        <ReferralCodeCard referralCode={referralCode} isLoading={!referralCode} />

        {/* Share Buttons */}
        <SocialShareButtons referralUrl={referralUrl} referralCode={referralCode} />

        {/* Print Materials - NEW FEATURE */}
        <PrintMaterialsCard referralCode={referralCode} />

        {/* Rewards Section */}
        <RewardsCard />

        {/* Qualification Methods */}
        <QualificationCard requirements={requirements} />

        {/* Recent Referrals */}
        <RecentReferrals referrals={stats?.referrals ?? []} maxItems={5} />

        {/* Tips Section */}
        <ReferralTips />
      </div>
    </div>
  );
}
